import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';

import { Badge, Box, Group, Skeleton, Tabs, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { CountryFlag } from '@/components/CountryFlag';
import { useLegalEntityContext } from '@/context/LegalEntityContext';

export const Route = createFileRoute('/_authenticated/company')({
  component: CompanyLayout,
});

function CompanyLayout() {
  const { t } = useTranslation();
  const { activeEntity, isLoading } = useLegalEntityContext();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const activeTab = pathname.startsWith('/company/staff')
    ? 'staff'
    : pathname.startsWith('/company/task-gen')
      ? 'task-gen'
      : pathname.startsWith('/company/quote')
        ? 'quote'
        : pathname.startsWith('/company/room-calculation')
          ? 'room-calculation'
          : pathname.startsWith('/company/milestones')
            ? 'milestones'
            : 'overview';

  if (isLoading)
    return (
      <Text p='xl' c='charcoal.5'>
        <Skeleton height={20} radius='sm' />
      </Text>
    );
  if (!activeEntity)
    return (
      <Text p='xl' c='brown.6'>
        {t('user.noEntity')}
      </Text>
    );

  return (
    <Box>
      <Box px='xl' pt='xl' pb={0}>
        <Group gap='sm' align='center' mb='lg'>
          <Box>
            <Group gap='sm' align='center'>
              <Title order={2} c='charcoal.9'>
                {activeEntity.name}
              </Title>
              <Badge color='forest'>{t('entities.status.active')}</Badge>
            </Group>
            <Text size='xs' c='charcoal.4' style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              <CountryFlag code={activeEntity.countryAlpha2Code} showName={false} size='1em' />
            </Text>
          </Box>
        </Group>

        <Tabs
          value={activeTab}
          onChange={(v) => {
            if (v === 'staff') void navigate({ to: '/company/staff' });
            else if (v === 'task-gen') void navigate({ to: '/company/task-gen' });
            else if (v === 'quote') void navigate({ to: '/company/quote' });
            else if (v === 'room-calculation') void navigate({ to: '/company/room-calculation' });
            else if (v === 'milestones') void navigate({ to: '/company/milestones' });
            else void navigate({ to: '/company' });
          }}
          styles={{ root: { '--tabs-color': 'var(--mantine-color-charcoal-9)' } }}
        >
          <Tabs.List>
            <Tabs.Tab value='overview'>{t('nav.overview')}</Tabs.Tab>
            <Tabs.Tab value='quote'>Devis</Tabs.Tab>
            <Tabs.Tab value='task-gen'>Tâches</Tabs.Tab>
            <Tabs.Tab value='room-calculation'>Calcul pièce</Tabs.Tab>
            <Tabs.Tab value='milestones'>Milestones</Tabs.Tab>
            <Tabs.Tab value='staff'>{t('nav.staff')}</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      <Outlet />
    </Box>
  );
}
